<template>
  <div class="finances-payouts-items-table finances-invoices-table">
    <PanelHeader
      title="Contractors"
      :isOpen="show"
      :onToggleButtonClick="togglePanel"
      titleClassName="col-md-8"
      buttonsWrapperClassName="col-md-4"
    >
      <AddItemButton :onClick="addItem">{{addPaymentButtonLabel}}</AddItemButton>
    </PanelHeader>

    <b-collapse v-model="show" class="mx-3">
      <div v-if="!$vModel.minLength || !$vModel.required" class="status-error text-center">Add at least one contractor</div>
      <EmptyTableMessage v-else-if="!items.length">
        There are no items yet. Use "{{addPaymentButtonLabel}}" button.
      </EmptyTableMessage>

      <fragment v-if="items.length">
        <b-table :fields="fields" :items="items" borderless>
          <template #cell(contractor)="{ item, index }">
            <div class="invalid-marker" v-if="item.isNotValid"></div>
            <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="item.contractor ? item.contractor.label : null">
              <multiselect
                class="multiselect__single-line"
                v-model="item.contractor"
                @input="payoutItemChanged(index, FieldNames.Contractor, $event)"
                :placeholder="currentChoice"
                :options="getContractorsSelectOptions"
                track-by="id"
                label="label"
                return="id"
                :showLabels="false"
                :disabled="!!currentContractor"
                allow-empty
              ></multiselect>
            </div>
          </template>
          <template #cell(project)="{ item, index }">
            <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="item.project ? item.project.label : null">
              <multiselect
                :class="{
                  'multiselect__single-line': true,
                  'is-invalid': (formSubmissionAttempted && $vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].project && $vModel.$each[index].project.$error) || getBackendError(index, 'project_key'),
                  'is-valid': shouldShowGreenBorder(index, 'project')
                }"
                v-model="item.project"
                @input="payoutItemChanged(index, FieldNames.Project, $event); clearBackendError(index, 'project_key')"
                :options="getProjectsListWithSelectedItems"
                track-by="label"
                label="label"
                return="key"
                :showLabels="false"
                :disabled="!item.contractor"
                allow-empty
              ></multiselect>
              <FieldErrorMessage
                v-if="$vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].project"
                :backend-error="getBackendError(index, 'project_key')"
                :show-frontend-error="formSubmissionAttempted && $vModel.$each[index].project.$error"
                frontend-message="Field is required"
              />
            </div>
          </template>
          <template #cell(hour_cost)="{ item, index }">
            <div class="cursor-pointer hour-cost">
            <span
              v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
              :title="item.compensation_currency_code ? item.compensation_currency_code : ''"
            >
              <b-form-input
                type="number"
                no-wheel
                v-model="item.hour_cost"
                @input="payoutItemChanged(index, FieldNames.HourCost, $event)"
              />
            </span>
              <span
                v-if="item.payment_type && item.payment_type === 'hourly'"
                class="payment-type-label"
                v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                title="Per hour">/h</span>
              <span
                v-else
                class="payment-type-label"
                v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                title="Per month">/m</span>
            </div>
          </template>
          <template #head(hours)>
            <span class="text-nowrap">{{fields.find(f => f.key === FieldNames.Hours).label}}</span>
          </template>
          <template #cell(hours)="{ item, index }">
          <span
            class="cursor-pointer"
            v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
            :title="item.amount ? 'Calculated cost: ' + item.amount : ''"
          >
            <b-form-input
              type="number"
              step="0.01"
              no-wheel
              v-model="item.hours"
              @input="payoutItemChanged(index, FieldNames.Hours, $event)"
            />
          </span>
          </template>
          <template #head(logged_hours)>
            <span class="text-nowrap">{{fields.find(f => f.key === FieldNames.LoggedHours).label}}</span>
          </template>
          <template #cell(logged_hours)="{ item, index }">
            <div style="margin-top: 8px;">{{ formatNumber(item.logged_hours) }}</div>
            <span class="cursor-pointer hours-info">
            <div
              v-if="item.hoursCalculating"
              class="hours-loader spinner-border text-secondary"
              role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </span>
          </template>
          <template #cell(calculateHours)="{ item, index }">
            <b-form-checkbox
              type="checkbox"
              size="lg"
              :value="item.calculateHours"
              @input="payoutItemChanged(index, FieldNames.CalculateHours, $event)"
              v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
              title="Auto calculate hours from worklogs"
              class="calculate-hours-checkbox"
            />
          </template>
          <template #cell(date_from)="{ item, index }">
            <b-form-group>
              <Datepicker
                :class="{
                  'small-paddings-input': true,
                  'is-invalid': (formSubmissionAttempted && $vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].date_from && $vModel.$each[index].date_from.$error) || getBackendError(index, 'date_from'),
                  'is-valid': shouldShowGreenBorder(index, 'date_from')
                }"
                :bootstrap-styling="true"
                :value="_toIsoStringTime(item.date_from)"
                @input="changeDate(item, $event, item.date_to, FieldNames.DateFrom, index); clearBackendError(index, 'date_from')"
                format="yyyy-MM-dd"
                v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                :title="item.created_periods ? 'Auto fill from the end of last period: ' + item.created_periods.date_from + ' - ' + item.created_periods.date_to : ''"
              />
              <FieldErrorMessage
                v-if="$vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].date_from"
                :backend-error="getBackendError(index, 'date_from')"
                :show-frontend-error="formSubmissionAttempted && $vModel.$each[index].date_from.$error"
                frontend-message="Field is required"
              />
            </b-form-group>
          </template>
          <template #cell(date_to)="{ item, index }">
            <b-form-group>
              <Datepicker
                :class="{
                  'small-paddings-input': true,
                  'is-invalid': (formSubmissionAttempted && $vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].date_to && $vModel.$each[index].date_to.$error) || getBackendError(index, 'date_to'),
                  'is-valid': shouldShowGreenBorder(index, 'date_to')
                }"
                :bootstrap-styling="true"
                :value="_toIsoStringTime(item.date_to)"
                @input="changeDate(item, $event, item.date_from, FieldNames.DateTo, index); clearBackendError(index, 'date_to')"
                format="yyyy-MM-dd"
              />
              <FieldErrorMessage
                v-if="$vModel && $vModel.$each && $vModel.$each[index] && $vModel.$each[index].date_to"
                :backend-error="getBackendError(index, 'date_to')"
                :show-frontend-error="formSubmissionAttempted && $vModel.$each[index].date_to.$error"
                frontend-message="Field is required"
              />
            </b-form-group>
          </template>
          <template #cell(actions)="data" v-if="items.length > 1">
            <b-button variant="danger" size="sm w-100" @click.prevent="removeItem(data)">
              <font-awesome-icon icon="trash" />
            </b-button>
          </template>
        </b-table>

        <div class="foot-note">
          <label>*</label> - dates are not involved in calculations, they are used only for manual data reconciliation
        </div>
      </fragment>
    </b-collapse>
  </div>
</template>

<script>
import moment from 'moment'
import FormItemsMixin from '../../../mixins/FormItemsMixin'
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import PanelHeader from '@components/invoices/form/PanelHeader.vue'
import AddItemButton from '@components/modals/FinanceInvoicesForm/components/AddItemButton.vue'
import EmptyTableMessage from '@components/invoices/EmptyTableMessage.vue'
import FieldErrorMessage from '@components/forms/FieldErrorMessage.vue'

export const FieldNames = {
  Contractor: 'contractor',
  Project: 'project',
  HourCost: 'hour_cost',
  Hours: 'hours',
  LoggedHours: 'logged_hours',
  CalculateHours: 'calculateHours',
  DateFrom: 'date_from',
  DateTo: 'date_to'
}

export default {
  components: { EmptyTableMessage, AddItemButton, PanelHeader, FieldErrorMessage },
  mixins: [FormItemsMixin],
  props: {
    /**
     * $vModel is a vuelidate copy of the passed to the component. We need it to correctly track user input to validate
     * data and mark fields as dirty.
     */
    $vModel: Object,
    currentContractor: {
      type: [Object, null],
      default: null
    },
    currentPartner: {
      type: [Object, null],
      default: null
    },
    currentChoice: {
      type: String,
      default: ''
    },
    formSubmissionAttempted: {
      type: Boolean,
      default: false
    },
    backendErrors: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    currentContractor: {
      deep: true,
      handler (v) {
        if (v && this.items.length === 0) {
          this.items[0] = { ...this.defaultItem }
          this.items[0].contractor = v
        }
        this.currentContractor = v
      }
    },
    currentPartner: {
      deep: true,
      handler (v) {
        this.currentPartner = v
      }
    },
    formSubmissionAttempted (newValue) {
      // Track field errors when form submission is attempted
      if (newValue) {
        this.trackFieldErrors()
      }
    },
    backendErrors: {
      deep: true,
      handler (newErrors) {
        // Track backend errors
        if (newErrors && Object.keys(newErrors).length > 0) {
          this.trackFieldErrors()
        }
      }
    }
  },
  data () {
    return {
      moment,
      FieldNames,
      addPaymentButtonLabel: 'Add item',
      fields: [
        { key: FieldNames.Contractor, label: 'Contractor', class: 'contractor-column' },
        { key: FieldNames.Project, label: 'Project', class: 'project-column' },
        { key: FieldNames.HourCost, label: 'Rate', class: 'cost-column' },
        { key: FieldNames.Hours, label: 'Approved h.', class: 'hours-column' },
        { key: FieldNames.LoggedHours, label: 'Logged h.', class: 'logged-hours-column' },
        { key: FieldNames.CalculateHours, label: 'Auto', class: 'calculate-hours-column' },
        { key: FieldNames.DateFrom, label: 'Date from*', class: 'date-from-column date-column' },
        { key: FieldNames.DateTo, label: 'Date to*', class: 'date-to-column date-column' },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: [],
      fieldsWithPreviousErrors: new Map() // Track fields with errors by item index and field name
    }
  },
  computed: {
    ...mapGetters({
      getProjectsWithVirtual: 'projects/getProjectsWithVirtual'
    }),
    getProjectsListWithSelectedItems () {
      const projectList = cloneDeep(this.getProjectsWithVirtual)
      for (const index in this.items) {
        const item = this.items[index]
        if (item.project) {
          const selectedProject = item.project
          const selectedProjectInList = projectList.find(function (project) {
            return project.label === selectedProject.label
          })
          if (selectedProjectInList === undefined) {
            if (selectedProject.isPending) {
              selectedProject.label = selectedProject.name + ' (Pending)'
            }
            projectList.push(selectedProject)
          }
        }
      }
      return projectList
    },
    defaultItem () {
      return {
        [FieldNames.Contractor]: this.currentContractor ?? null,
        [FieldNames.Project]: null,
        [FieldNames.Hours]: 0,
        [FieldNames.LoggedHours]: 0,
        [FieldNames.HourCost]: 0,
        [FieldNames.DateFrom]: null,
        [FieldNames.DateTo]: null,
        [FieldNames.CalculateHours]: true,
        'hoursCalculating': false,
        'isNotValid': true
      }
    },
    getContractorsSelectOptions () {
      return this._getPartnerRelatedUsers(this.currentPartner ? this.currentPartner.id : null)
    },
    isPartnerChoice () {
      return this.currentChoice === 'Partner'
    }
  },
  methods: {
    changeDate (item, value, oppositePeriodVlue, fieldName, index) {
      if (!moment(value).isSame(oppositePeriodVlue, 'month') && oppositePeriodVlue !== null) {
        this.$snotify.error(
          'Period should be in same month. Please add another item for different month or use Auto Fill',
          'Error',
          this.$store.state.snotify
        )
        this.$vModel.$each[index][fieldName].$model = value
        this.items[index].isNotValid = true
        return
      }
      this.items[index].isNotValid = false
      this.payoutItemChanged(index, fieldName, value)
    },
    payoutItemChanged (index, fieldName, attrValue) {
      const previousItem = cloneDeep(this.items[index])
      if (fieldName === FieldNames.DateFrom || fieldName === FieldNames.DateTo) {
        attrValue = this.convertDataToString(attrValue)
      }

      /**
       * Here we set values not directly to the item, but rather through the vuelidate model to allow vuelidate to mark
       * these fields as dirty. That mean that these field are changed in response to user input and not programmatically.
       */
      this.$vModel.$each[index][fieldName].$model = attrValue

      const item = this.items[index]

      if (fieldName === FieldNames.Contractor) {
        item.project = null
      }
      item.amount = null

      if (item.contractor === null || item.project === null || item.date_from === null || item.date_to === null) {
        item.isNotValid = true
        return
      }

      if ([FieldNames.Contractor, FieldNames.Project, FieldNames.CalculateHours, FieldNames.DateFrom, FieldNames.DateTo].includes(fieldName)) {
        this.$emit('calculateHours', { item, previousItem, index })
      } else if ([FieldNames.Hours, FieldNames.HourCost].includes(fieldName)) {
        this.$emit('calculatePayout')
      }
    },
    removeItem (data) {
      // Clear error tracking for removed item
      const requiredFields = ['project', 'date_from', 'date_to']
      requiredFields.forEach(fieldName => {
        const errorKey = `${data.index}_${fieldName}`
        this.fieldsWithPreviousErrors.delete(errorKey)
      })

      // Re-index error tracking for remaining items
      const newErrorTracking = new Map()
      this.fieldsWithPreviousErrors.forEach((value, key) => {
        const [itemIndex, fieldName] = key.split('_')
        const indexNum = parseInt(itemIndex)
        if (indexNum > data.index) {
          // Shift index down for items after the removed one
          const newKey = `${indexNum - 1}_${fieldName}`
          newErrorTracking.set(newKey, value)
        } else if (indexNum < data.index) {
          // Keep existing index for items before the removed one
          newErrorTracking.set(key, value)
        }
        // Skip the removed item (indexNum === data.index)
      })
      this.fieldsWithPreviousErrors = newErrorTracking

      this.items = this.items.filter((item, index) => index !== data.index)
      this.triggerInputEmit()
      this.$emit('calculatePayout')
    },
    formatNumber (number) {
      return number ? parseFloat(number).toString().replace() : ''
    },
    clearBackendError (itemIndex, fieldName) {
      const errorKey = `items.${itemIndex}.${fieldName}`
      if (this.backendErrors[errorKey]) {
        // Create a copy to trigger reactivity
        const updatedErrors = { ...this.backendErrors }
        delete updatedErrors[errorKey]
        this.$emit('updateBackendErrors', updatedErrors)
      }
    },
    getBackendError (itemIndex, fieldName) {
      const errorKey = `items.${itemIndex}.${fieldName}`
      const error = this.backendErrors[errorKey]
      return error ? (Array.isArray(error) ? error[0] : error) : null
    },
    shouldShowGreenBorder (itemIndex, fieldName) {
      // Only show green border if the field had errors before and is now valid
      const vModelField = this.$vModel?.$each?.[itemIndex]?.[fieldName]
      const hasError = (this.formSubmissionAttempted && vModelField?.$error) || this.getBackendError(itemIndex, fieldName)

      const errorKey = `${itemIndex}_${fieldName}`
      const hadPreviousError = this.fieldsWithPreviousErrors.has(errorKey)
      const isDirty = vModelField?.$dirty

      return hadPreviousError && !hasError && isDirty && this.formSubmissionAttempted
    },
    trackFieldErrors () {
      // Track which fields currently have errors for each item
      const requiredFields = ['project_key', 'date_from', 'date_to']

      this.items.forEach((item, index) => {
        requiredFields.forEach(fieldName => {
          const frontendFieldName = fieldName === 'project_key' ? 'project' : fieldName
          const vModelField = this.$vModel?.$each?.[index]?.[frontendFieldName]
          const hasError = (this.formSubmissionAttempted && vModelField?.$error) || this.getBackendError(index, fieldName)

          const errorKey = `${index}_${frontendFieldName}`
          if (hasError) {
            this.fieldsWithPreviousErrors.set(errorKey, true)
          }
        })
      })
    },
    clearErrorTracking () {
      // Clear all error tracking (called when parent form is reset)
      this.fieldsWithPreviousErrors.clear()
    }
  }
}
</script>

<style lang="scss" scoped>
.finances-payouts-items-table {
  margin-bottom: 1rem;

  :global(.contractor-column) {
    min-width: 100px;
    max-width: 0;
  }

  :global(.project-column) {
    min-width: 100px;
    max-width: 0;
  }

  :global(.logged-hours-column) {
    position: relative;
    width: 5%;
    padding: 0;
    text-align: center;
  }

  :global(.calculate-hours-column) {
    text-align: center;
  }

  :global(.date-column) {
    min-width: 114px;
  }

  .hour-cost {
    position: relative;
  }

  .hour-cost input{
    padding-right: 20px;
  }

  .payment-type-label {
    color: grey;
    display: block;
    position: absolute;
    top: 8px;
    right: 5px;
    font-size: 11px;
  }

  .hours-info{
    position:absolute;
    left: 17px;
    top: 3px;
  }

  .hours-loader{
    margin-top: 9px;
  }

  .invalid-marker {
    position: absolute;
    z-index: 1;
    height: 32px;
    border-left: 4px solid rgba(255, 0, 0, 0.5);
  }

  .foot-note {
    margin-left: 5px;

    label {
      margin-bottom: 0;
    }
  }
}

.calculate-hours-checkbox label {
  transform: translateX(5px);
}

// Validation styling for Datepicker component
:global(.vdp-datepicker.is-invalid input) {
  border-color: #DC3545 !important;
  background-image: none !important;
  padding-right: 12px !important;
}

:global(.vdp-datepicker.is-valid input) {
  border-color: #3FA52F !important;
  background-image: none !important;
  padding-right: 12px !important;
}
</style>
